import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import Layout from '../Layout'
import { useLocale } from '../../../lib/LocaleContext'

/**
 * Two-Factor Authentication settings panel.
 *
 * Handles three states:
 *  1. Not enrolled  — shows "Enable 2FA" wizard trigger
 *  2. Enrollment in progress — shows QR code + confirm step
 *  3. Enrolled — shows status, backup code count, disable + regenerate actions
 */
export default function TwoFactor({
  enrolled = false,
  enrolled_at = null,
  backup_codes_remaining = 0,
  provisioning = null,
  backup_codes_once = null,
  step = null,
  errors = {},
}) {
  const { props } = usePage()
  const flash = props.flash || {}
  const { t } = useLocale()

  const [confirmCode, setConfirmCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [regenCode, setRegenCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showDisable, setShowDisable] = useState(false)
  const [showRegen, setShowRegen] = useState(false)
  const [codesRevealed, setCodesRevealed] = useState(false)

  function handleStart(e) {
    e.preventDefault()
    setSubmitting(true)
    router.post('/settings/security/two_factor/start', {}, {
      onFinish: () => setSubmitting(false),
    })
  }

  function handleConfirm(e) {
    e.preventDefault()
    setSubmitting(true)
    router.post('/settings/security/two_factor/confirm', { code: confirmCode }, {
      onFinish: () => { setSubmitting(false); setConfirmCode('') },
    })
  }

  function handleDisable(e) {
    e.preventDefault()
    setSubmitting(true)
    router.delete('/settings/security/two_factor', { data: { code: disableCode } }, {
      onFinish: () => { setSubmitting(false); setDisableCode('') },
    })
  }

  function handleRegen(e) {
    e.preventDefault()
    setSubmitting(true)
    router.post('/settings/security/two_factor/backup_codes', { code: regenCode }, {
      onFinish: () => { setSubmitting(false); setRegenCode('') },
    })
  }

  return (
    <Layout>
      <div className="max-w-xl">
        <h2 className="text-zinc-200 font-mono text-base font-semibold mb-1">
          {t('settings.security.two_factor.title')}
        </h2>
        <p className="text-zinc-500 font-mono text-xs mb-6">
          {t('settings.security.two_factor.description')}
        </p>

        {flash.notice && (
          <div className="mb-4 px-4 py-2 bg-emerald-950 border border-emerald-800 rounded-lg text-sm text-emerald-400 font-mono">
            {flash.notice}
          </div>
        )}
        {flash.alert && (
          <div className="mb-4 px-4 py-2 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400 font-mono">
            {flash.alert}
          </div>
        )}

        {/* ───── Backup codes — shown exactly once after enrollment or regeneration ───── */}
        {backup_codes_once && (
          <div className="mb-6 bg-amber-950/50 border border-amber-800 rounded-xl p-5">
            <p className="text-amber-300 font-mono text-xs font-semibold mb-3 uppercase tracking-wider">
              {t('settings.security.two_factor.backup_codes_warning')}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backup_codes_once.map((code, i) => (
                <code
                  key={i}
                  className="block bg-zinc-900/60 border border-zinc-700 rounded px-3 py-1.5 text-sm font-mono text-zinc-200 text-center tracking-widest"
                >
                  {code}
                </code>
              ))}
            </div>
            <p className="text-amber-500 font-mono text-[10px]">
              {t('settings.security.two_factor.backup_codes_hint')}
            </p>
          </div>
        )}

        {/* ───── Enrollment wizard (QR code + confirm) ───── */}
        {step === 'confirm' && provisioning && (
          <div className="mb-6 bg-zinc-800/30 border border-zinc-600/50 rounded-xl p-5">
            <p className="text-zinc-300 font-mono text-xs mb-4">
              {t('settings.security.two_factor.scan_instruction')}
            </p>

            <div
              className="flex justify-center mb-5 rounded-lg overflow-hidden bg-white p-3 w-fit mx-auto"
              dangerouslySetInnerHTML={{ __html: provisioning.qr_svg }}
            />

            <p className="text-zinc-500 font-mono text-[10px] text-center mb-4 break-all">
              {provisioning.otpauth_uri}
            </p>

            {errors.code && (
              <p className="text-red-400 font-mono text-xs mb-3">{errors.code}</p>
            )}

            <form onSubmit={handleConfirm} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={t('settings.security.two_factor.otp_placeholder')}
                maxLength={6}
                autoFocus
                className="w-full bg-zinc-800/60 border border-zinc-600/60 rounded-lg px-3 py-2.5 text-lg font-mono text-zinc-100 placeholder-zinc-600 text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-600 transition-colors"
              />
              <button
                type="submit"
                disabled={submitting || confirmCode.length < 6}
                className="w-full bg-emerald-800/60 hover:bg-emerald-700/80 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-200 border border-emerald-700/40 font-mono text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors"
              >
                {submitting ? t('settings.security.two_factor.confirming') : t('settings.security.two_factor.confirm_submit')}
              </button>
            </form>
          </div>
        )}

        {/* ───── Not enrolled + not in QR step ───── */}
        {!enrolled && step !== 'confirm' && !backup_codes_once && (
          <div className="bg-zinc-800/30 border border-zinc-600/50 rounded-xl p-5">
            <p className="text-zinc-400 font-mono text-xs mb-4">
              {t('settings.security.two_factor.not_enabled')}
            </p>
            <form onSubmit={handleStart}>
              <button
                type="submit"
                disabled={submitting}
                className="bg-emerald-800/60 hover:bg-emerald-700/80 disabled:opacity-50 text-emerald-200 border border-emerald-700/40 font-mono text-sm font-semibold py-2 px-5 rounded-lg transition-colors"
              >
                {submitting ? t('settings.security.two_factor.enabling') : t('settings.security.two_factor.enable')}
              </button>
            </form>
          </div>
        )}

        {/* ───── Enrolled status panel ───── */}
        {enrolled && step !== 'confirm' && (
          <div className="space-y-4">
            <div className="bg-zinc-800/30 border border-zinc-600/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-emerald-400 font-mono text-xs font-semibold uppercase tracking-wider">
                  {t('settings.security.two_factor.enabled_label')}
                </span>
              </div>
              {enrolled_at && (
                <p className="text-zinc-500 font-mono text-xs">
                  {t('settings.security.two_factor.enrolled_on', {
                    date: new Date(enrolled_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  })}
                </p>
              )}
              <p className="text-zinc-500 font-mono text-xs mt-1">
                {backup_codes_remaining !== 1
                  ? t('settings.security.two_factor.backup_remaining_plural', { count: backup_codes_remaining })
                  : t('settings.security.two_factor.backup_remaining', { count: backup_codes_remaining })
                }
              </p>
            </div>

            {/* Disable 2FA */}
            <div className="bg-zinc-800/30 border border-zinc-600/50 rounded-xl p-5">
              {!showDisable ? (
                <button
                  onClick={() => setShowDisable(true)}
                  className="text-red-400 hover:text-red-300 font-mono text-xs border border-red-900/50 hover:border-red-800 rounded-lg px-4 py-2 transition-colors"
                >
                  {t('settings.security.two_factor.disable_title')}
                </button>
              ) : (
                <form onSubmit={handleDisable} className="space-y-3">
                  <p className="text-zinc-400 font-mono text-xs">
                    {t('settings.security.two_factor.disable_instruction')}
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={disableCode}
                    onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('settings.security.two_factor.otp_placeholder')}
                    maxLength={6}
                    autoFocus
                    className="w-full bg-zinc-800/60 border border-zinc-600/60 rounded-lg px-3 py-2 text-base font-mono text-zinc-100 placeholder-zinc-600 text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-700 transition-colors"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={submitting || disableCode.length < 6}
                      className="flex-1 bg-red-900/60 hover:bg-red-800/80 disabled:opacity-50 text-red-200 border border-red-800/40 font-mono text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      {submitting ? t('settings.security.two_factor.disabling') : t('settings.security.two_factor.disable_confirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowDisable(false); setDisableCode('') }}
                      className="px-4 py-2 text-zinc-400 hover:text-zinc-200 font-mono text-xs rounded-lg border border-zinc-700 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Regenerate backup codes */}
            <div className="bg-zinc-800/30 border border-zinc-600/50 rounded-xl p-5">
              {!showRegen ? (
                <button
                  onClick={() => setShowRegen(true)}
                  className="text-amber-400 hover:text-amber-300 font-mono text-xs border border-amber-900/50 hover:border-amber-800 rounded-lg px-4 py-2 transition-colors"
                >
                  {t('settings.security.two_factor.regen_title')}
                </button>
              ) : (
                <form onSubmit={handleRegen} className="space-y-3">
                  <p className="text-zinc-400 font-mono text-xs">
                    {t('settings.security.two_factor.regen_instruction')}
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={regenCode}
                    onChange={e => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('settings.security.two_factor.otp_placeholder')}
                    maxLength={6}
                    autoFocus
                    className="w-full bg-zinc-800/60 border border-zinc-600/60 rounded-lg px-3 py-2 text-base font-mono text-zinc-100 placeholder-zinc-600 text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-700 transition-colors"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={submitting || regenCode.length < 6}
                      className="flex-1 bg-amber-900/60 hover:bg-amber-800/80 disabled:opacity-50 text-amber-200 border border-amber-800/40 font-mono text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      {submitting ? t('settings.security.two_factor.regenerating') : t('settings.security.two_factor.regen_confirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowRegen(false); setRegenCode('') }}
                      className="px-4 py-2 text-zinc-400 hover:text-zinc-200 font-mono text-xs rounded-lg border border-zinc-700 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
